import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  Typography,
  Chip,
  Box,
  Divider,
  LinearProgress,
  Tooltip,
  Badge
} from "@mui/material";
import { withStyles } from "@mui/styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import Footer from "../../components/footer";
import HeaderMenu from "../../components/header-menu";
import CustomizedSnackbars from "../../components/material-snackbars";
import Api from "../../services/api";
import {
  getProviderPredefinedMessageTemplates,
  updateProviderPredefinedMessageTemplates
} from "../../services/2talk/provider-predefined-message-templates.service";
import { Overlay } from "../../styles/global";
import { Styles } from "../../styles/material-styles";

// ──────────────────────────────────────────────────────────────────────────────
// PlaceholderProtectedTextarea
// Renders a contenteditable div that highlights placeholders in blue and
// prevents the user from deleting any required placeholder token.
// ──────────────────────────────────────────────────────────────────────────────
const PLACEHOLDER_RE = /(\{\{[^}]+\}\}|\{min\}|\{max\})/g;

function buildHTML(text) {
  if (!text) return "";
  return text.replace(PLACEHOLDER_RE, (match) =>
    `<span class="ph-chip" contenteditable="false" data-ph="${match}">${match}</span>`
  );
}

function extractText(node) {
  let text = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      text += child.textContent;
    } else if (child.dataset && child.dataset.ph) {
      text += child.dataset.ph;
    } else {
      text += extractText(child);
    }
  });
  return text;
}

class PlaceholderProtectedTextarea extends Component {
  constructor(props) {
    super(props);
    this.ref = createRef();
    this._ignoreNextInput = false;
  }

  hasAllRequiredPlaceholders = (text) => {
    const required = this.props.requiredPlaceholders || [];
    if (required.length === 0) return true;

    const normalized = text || "";
    return required.every((placeholder) => normalized.includes(placeholder));
  };

  componentDidMount() {
    this.updateHTML(this.props.value);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this._ignoreNextInput === false) {
      const sel = window.getSelection();
      let offset = null;
      if (sel && sel.rangeCount && this.ref.current && this.ref.current.contains(sel.anchorNode)) {
        offset = this.getCaretCharOffset();
      }
      this.updateHTML(this.props.value);
      if (offset !== null) this.setCaretCharOffset(offset);
    }
  }

  updateHTML(value) {
    if (this.ref.current) {
      this.ref.current.innerHTML = buildHTML(value || "");
    }
  }

  getCaretCharOffset() {
    const el = this.ref.current;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    const range = sel.getRangeAt(0).cloneRange();
    range.selectNodeContents(el);
    range.setEnd(sel.anchorNode, sel.anchorOffset);
    return range.toString().length;
  }

  setCaretCharOffset(offset) {
    const el = this.ref.current;
    const sel = window.getSelection();
    if (!sel) return;
    let charCount = 0;
    let targetNode = null;
    let targetOffset = 0;

    const walk = (node) => {
      if (targetNode) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const len = node.textContent.length;
        if (charCount + len >= offset) {
          targetNode = node;
          targetOffset = offset - charCount;
        } else {
          charCount += len;
        }
      } else {
        node.childNodes.forEach(walk);
      }
    };
    walk(el);
    if (!targetNode) return;
    const range = document.createRange();
    range.setStart(targetNode, targetOffset);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  handleKeyDown = (e) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    const isCollapsed = range.collapsed;
    const selectionHasChip = !isCollapsed && !!range.cloneContents().querySelector("[data-ph]");

    // Helper: get the nearest ph-chip touching the caret
    const adjacentChip = (direction) => {
      if (!isCollapsed) return null;
      const { anchorNode, anchorOffset } = sel;
      if (direction === "backward") {
        // chip immediately before caret?
        if (anchorOffset === 0) {
          const prev = anchorNode.previousSibling;
          if (prev && prev.dataset && prev.dataset.ph) return prev;
        }
      } else {
        // chip immediately after caret?
        const textLen = anchorNode.textContent ? anchorNode.textContent.length : 0;
        if (anchorOffset === textLen || anchorNode.nodeType !== Node.TEXT_NODE) {
          const next = anchorNode.nextSibling;
          if (next && next.dataset && next.dataset.ph) return next;
        }
      }
      return null;
    };

    // If a selection contains any placeholder chip, block direct replacement typing.
    if (selectionHasChip) {
      const isPrintable = e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (isPrintable || e.key === "Enter") {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Backspace") {
      // Check if selection contains a chip
      if (!isCollapsed) {
        const fragment = range.cloneContents();
        const hasChip = fragment.querySelector("[data-ph]");
        if (hasChip) { e.preventDefault(); return; }
      } else if (adjacentChip("backward")) {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Delete") {
      if (!isCollapsed) {
        const fragment = range.cloneContents();
        const hasChip = fragment.querySelector("[data-ph]");
        if (hasChip) { e.preventDefault(); return; }
      } else if (adjacentChip("forward")) {
        e.preventDefault();
        return;
      }
    }

    // Prevent cutting a selection that contains chips
    if ((e.key === "x" && (e.ctrlKey || e.metaKey)) && !isCollapsed) {
      const fragment = range.cloneContents();
      if (fragment.querySelector("[data-ph]")) { e.preventDefault(); return; }
    }
  };

  handleInput = () => {
    if (!this.ref.current) return;
    const currentText = this.props.value || "";
    const nextText = extractText(this.ref.current);

    // Hard guarantee: once required placeholders are present, they cannot be removed.
    if (this.hasAllRequiredPlaceholders(currentText) && !this.hasAllRequiredPlaceholders(nextText)) {
      const caretOffset = this.getCaretCharOffset();
      this._ignoreNextInput = true;
      this.updateHTML(currentText);
      this.setCaretCharOffset(Math.min(caretOffset, currentText.length));
      setTimeout(() => { this._ignoreNextInput = false; }, 0);
      return;
    }

    this._ignoreNextInput = true;
    this.props.onChange(nextText);
    setTimeout(() => { this._ignoreNextInput = false; }, 0);
  };

  handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  handleCut = (e) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const hasChip = !!range.cloneContents().querySelector("[data-ph]");
    if (hasChip) {
      e.preventDefault();
    }
  };

  handleDrop = (e) => {
    e.preventDefault();
  };

  render() {
    const { error, disabled, placeholder } = this.props;

    return (
      <div style={{ position: "relative" }}>
        <style>{`
          .ph-editor-wrap {
            border: 1px solid ${error ? "var(--status-error)" : "var(--border-color)"};
            border-radius: 6px;
            transition: border-color 0.2s, box-shadow 0.2s;
            background: var(--mexx-bg-secondary);
          }
          .ph-editor-wrap:focus-within {
            border-color: ${error ? "var(--status-error)" : "var(--mexx-green-primary)"};
            box-shadow: 0 0 0 2px ${error ? "var(--status-error-bg)" : "var(--mexx-green-subtle)"};
          }
          .ph-editor {
            min-height: 96px;
            padding: 12px 14px;
            font-size: 14px;
            font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
            line-height: 1.7;
            outline: none;
            white-space: pre-wrap;
            word-break: break-word;
            color: var(--text-primary);
          }
          .ph-editor:empty:before {
            content: attr(data-placeholder);
            color: var(--text-muted);
            pointer-events: none;
          }
          .ph-chip {
            display: inline-flex;
            align-items: center;
            background: var(--mexx-green-subtle);
            color: var(--mexx-green-primary);
            border: 1px solid var(--border-color-focus);
            border-radius: 4px;
            padding: 1px 6px;
            font-size: 12.5px;
            font-family: 'JetBrains Mono', 'Fira Code', monospace;
            font-weight: 600;
            margin: 0 1px;
            cursor: not-allowed;
            user-select: none;
            vertical-align: middle;
          }
        `}</style>
        <div className="ph-editor-wrap">
          <div
            ref={this.ref}
            className="ph-editor"
            contentEditable={!disabled}
            suppressContentEditableWarning
            data-placeholder={placeholder || "Digite a mensagem..."}
            onKeyDown={this.handleKeyDown}
            onInput={this.handleInput}
            onPaste={this.handlePaste}
            onCut={this.handleCut}
            onDrop={this.handleDrop}
          />
        </div>
        {error && (
          <Typography variant="caption" style={{ color: "var(--status-error)", marginTop: 4, display: "block", paddingLeft: 2 }}>
            {error}
          </Typography>
        )}
      </div>
    );
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page component
// ──────────────────────────────────────────────────────────────────────────────
class ProviderPredefinedMessageTemplates extends Component {
  constructor(props) {
    super(props);

    this.isAdmGlobal = false;

    const { userSession } = this.props;

    if (userSession == null || userSession.user == null || userSession.user.userrole == null) {
      Api.logoff();
    } else {
      userSession.user.userrole.map((item) => {
        if (item.role.roletype == 0) {
          this.isAdmGlobal = true;
        }
      });
    }

    this.state = {
      loading: false,
      saving: false,
      openNotification: false,
      notificationVariant: "error",
      notificationMessage: "",
      items: [],
      initialMessagesById: {},
      fieldErrors: {},
      activeCategory: "Todos"
    };
  }

  componentDidMount() {
    this.loadConfiguration();
  }

  extractErrorMessage = (resultOrError, fallbackMessage) => {
    if (resultOrError?.errors?.length > 0) return resultOrError.errors[0];
    if (resultOrError?.response?.data?.errors?.length > 0) return resultOrError.response.data.errors[0];
    if (resultOrError?.data?.errors?.length > 0) return resultOrError.data.errors[0];
    return fallbackMessage;
  };

  getMissingPlaceholders = (requiredPlaceholders, message) => {
    const normalizedMessage = message || "";
    return (requiredPlaceholders || []).filter((p) => !normalizedMessage.includes(p));
  };

  getFieldError = (item) => {
    const missing = this.getMissingPlaceholders(item.requiredPlaceholders, item.message);
    return missing.length === 0 ? "" : `Placeholders obrigatórios ausentes: ${missing.join(", ")}`;
  };

  buildFieldErrors = (items) => {
    const errors = {};
    (items || []).forEach((item) => {
      const msg = this.getFieldError(item);
      if (msg) errors[item.id] = msg;
    });
    return errors;
  };

  buildInitialMessagesById = (items) => {
    const map = {};
    (items || []).forEach((item) => { map[item.id] = item.message || ""; });
    return map;
  };

  loadConfiguration = async () => {
    this.setState({ loading: true });
    try {
      const result = await getProviderPredefinedMessageTemplates();
      if (result?.success) {
        const apiItems = Array.isArray(result.data) ? result.data : [];
        const items = apiItems
          .map((apiItem) => {
            const id = Number(apiItem?.id || 0);
            if (id <= 0) {
              return null;
            }

            return {
              id,
              grouptype: Number(apiItem?.grouptype || 0),
              groupkey: typeof apiItem?.groupkey === "string" ? apiItem.groupkey : "",
              grouplabel: typeof apiItem?.grouplabel === "string" ? apiItem.grouplabel : "",
              label: apiItem?.label || `Mensagem ${id}`,
              message: typeof apiItem?.message === "string" ? apiItem.message : "",
              requiredPlaceholders: Array.isArray(apiItem?.requiredplaceholders)
                ? apiItem.requiredplaceholders
                : []
            };
          })
          .filter((item) => item !== null)
          .sort((a, b) => a.id - b.id);

        this.setState({
          items,
          initialMessagesById: this.buildInitialMessagesById(items),
          fieldErrors: this.buildFieldErrors(items),
          loading: false
        });
        return;
      }
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: this.extractErrorMessage(result, "Não foi possível carregar as mensagens.")
      });
    } catch (error) {
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: this.extractErrorMessage(error, "Não foi possível carregar as mensagens.")
      });
      Api.kickoff(error);
    }
  };

  handleMessageChange = (id, message) => {
    this.setState((prev) => {
      const items = prev.items.map((item) =>
        item.id === id ? { ...item, message } : item
      );
      return { items, fieldErrors: this.buildFieldErrors(items) };
    });
  };

  closeNotification = () => this.setState({ openNotification: false });

  canSave = () => {
    const { items, fieldErrors, loading, saving } = this.state;
    return (
      !loading &&
      !saving &&
      items.length > 0 &&
      Object.keys(fieldErrors).length === 0 &&
      this.getChangedItems().length > 0
    );
  };

  getChangedItems = () =>
    this.state.items
      .filter((item) => (this.state.initialMessagesById[item.id] || "") !== (item.message || ""))
      .map(({ id, message }) => ({ id, message }));

  handleSubmit = async () => {
    const fieldErrors = this.buildFieldErrors(this.state.items);
    if (Object.keys(fieldErrors).length > 0) {
      this.setState({ fieldErrors, openNotification: true, notificationVariant: "error", notificationMessage: "Corrija os placeholders obrigatórios antes de salvar." });
      return;
    }
    const payload = this.getChangedItems();
    if (payload.length === 0) {
      this.setState({ openNotification: true, notificationVariant: "success", notificationMessage: "Nenhuma alteração encontrada para salvar." });
      return;
    }
    this.setState({ saving: true });
    try {
      const result = await updateProviderPredefinedMessageTemplates(payload);
      if (result?.success) {
        const initialMessagesById = { ...this.state.initialMessagesById };
        payload.forEach((item) => { initialMessagesById[item.id] = item.message; });
        this.setState({ saving: false, initialMessagesById, openNotification: true, notificationVariant: "success", notificationMessage: "Mensagens atualizadas com sucesso." });
        return;
      }
      this.setState({ saving: false, openNotification: true, notificationVariant: "error", notificationMessage: this.extractErrorMessage(result, "Não foi possível salvar as mensagens.") });
    } catch (error) {
      this.setState({ saving: false, openNotification: true, notificationVariant: "error", notificationMessage: this.extractErrorMessage(error, "Não foi possível salvar as mensagens.") });
      Api.kickoff(error);
    }
  };

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const { items, fieldErrors, loading, saving, activeCategory } = this.state;

    if (!this.isAdmGlobal) {
      const language = this.props.match.params.lang;
      document.location.href = `/${language}/unavailable`;
    }

    if (Api == null) {
      const language = this.props.match.params.lang;
      document.location.href = `/${language}/unavailable`;
    }

    const groupMetaByKey = items.reduce((acc, item) => {
      if (!item.groupkey) {
        return acc;
      }

      const existing = acc[item.groupkey];
      const nextGroupType = Number(item.grouptype || 0);
      if (!existing || nextGroupType < existing.grouptype) {
        acc[item.groupkey] = {
          groupkey: item.groupkey,
          grouptype: nextGroupType,
          grouplabel: item.grouplabel || item.groupkey
        };
      }

      return acc;
    }, {});

    const sortedGroups = Object.values(groupMetaByKey)
      .sort((a, b) => {
        if (a.grouptype !== b.grouptype) {
          return a.grouptype - b.grouptype;
        }

        return (a.grouplabel || "").localeCompare(b.grouplabel || "");
      });

    const categories = ["Todos", ...sortedGroups.map((group) => group.groupkey)];
    const visibleItems = activeCategory === "Todos"
      ? items
      : items.filter((item) => item.groupkey === activeCategory);

    const changedCount = this.getChangedItems().length;
    const errorCount = Object.keys(fieldErrors).length;
    const progress = items.length > 0
      ? Math.round((items.filter((i) => (i.message || "").trim().length > 0).length / items.length) * 100)
      : 0;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />

        <main
          className={clsx(
            classes.content,
            { [classes.contentShift]: headerMenu.open },
            classes.actionsContent,
            { [classes.actionscontentShift]: actionsHeaderMenu.open }
          )}
        >
          <div className={classes.drawerHeader} />

          <Container component="main" maxWidth="xl" style={{ maxWidth: "100%", paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />

            {/* ── Header ─────────────────────────────────────────── */}
            <Box
              sx={{
                borderRadius: "12px",
                p: 3,
                mb: 3,
                color: "var(--text-primary)",
                backgroundColor: "var(--mexx-bg-elevated)",
                border: "var(--border-default)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                boxShadow: "var(--shadow-sm)"
              }}
            >
              <Box>
                <Typography variant="h5" style={{ fontWeight: 700, letterSpacing: "-0.3px" }}>
                  Mensagens de Modelo do Provedor
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8, marginTop: 4 }}>
                  Edite os textos e mantenha os placeholders obrigatórios — eles ficam destacados e não podem ser removidos.
                </Typography>

                {/* Progress bar */}
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      width: 180,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "var(--mexx-green-subtle)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "var(--mexx-green-primary)",
                        borderRadius: 3
                      }
                    }}
                  />
                  <Typography variant="caption" style={{ opacity: 0.85 }}>
                    {progress}% preenchido
                  </Typography>
                  {errorCount > 0 && (
                    <Chip
                      icon={<ErrorOutlineIcon style={{ fontSize: 14, color: "var(--text-on-primary)" }} />}
                      label={`${errorCount} erro${errorCount > 1 ? "s" : ""}`}
                      size="small"
                      sx={{ bgcolor: "var(--status-error)", color: "var(--text-on-primary)", height: 22, fontSize: 11 }}
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Tooltip title="Recarregar configurações">
                  <span>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<RefreshIcon />}
                      disabled={loading || saving}
                      onClick={this.loadConfiguration}
                      sx={{
                        color: "var(--mexx-green-primary)",
                        borderColor: "var(--mexx-green-primary)",
                        "&:hover": {
                          borderColor: "var(--mexx-green-primary)",
                          backgroundColor: "var(--mexx-green-subtle)"
                        }
                      }}
                    >
                      Recarregar
                    </Button>
                  </span>
                </Tooltip>

                <Badge badgeContent={changedCount || null} color="warning">
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!this.canSave()}
                    onClick={this.handleSubmit}
                    sx={{
                      bgcolor: "var(--mexx-green-primary)",
                      color: "var(--text-on-primary)",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "var(--mexx-green-dark)" },
                      "&:disabled": { bgcolor: "var(--mexx-gray-300)", color: "var(--text-muted)" }
                    }}
                  >
                    Salvar
                  </Button>
                </Badge>
              </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

            {/* ── Category filter tabs ───────────────────────────── */}
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              {categories.map((cat) => {
                const isActive = activeCategory === cat;
                const groupMeta = cat === "Todos" ? null : groupMetaByKey[cat];
                const label = cat === "Todos" ? "Todos" : (groupMeta?.grouplabel || cat);
                const count = cat === "Todos" ? items.length : items.filter((i) => i.groupkey === cat).length;
                const errorsInCat = cat === "Todos"
                  ? errorCount
                  : items.filter((i) => i.groupkey === cat && fieldErrors[i.id]).length;
                return (
                  <Chip
                    key={cat}
                    label={`${label} (${count})`}
                    onClick={() => this.setState({ activeCategory: cat })}
                    icon={errorsInCat > 0 ? <ErrorOutlineIcon style={{ color: isActive ? "var(--text-on-primary)" : "var(--status-error)", fontSize: 15 }} /> : undefined}
                    sx={{
                      bgcolor: isActive ? "var(--mexx-green-primary)" : "transparent",
                      color: isActive ? "var(--text-on-primary)" : "var(--text-primary)",
                      border: "1.5px solid",
                      borderColor: isActive ? "var(--mexx-green-primary)" : "var(--border-color)",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 13,
                      "&:hover": {
                        bgcolor: isActive ? "var(--mexx-green-primary)" : "var(--mexx-green-subtle)"
                      },
                      transition: "all 0.15s"
                    }}
                  />
                );
              })}
            </Box>

            {/* ── Field list ────────────────────────────────────── */}
            <Grid container spacing={2}>
              {visibleItems.map((item) => {
                const error = fieldErrors[item.id];
                const isChanged = (this.state.initialMessagesById[item.id] || "") !== (item.message || "");
                const catLabel = item.grouplabel || item.groupkey || "Grupo";

                return (
                  <Grid item xs={12} md={6} key={item.id}>
                    <Card
                      sx={{
                        borderRadius: "10px",
                        border: error
                          ? "1.5px solid var(--status-error)"
                          : isChanged
                          ? "1.5px solid var(--mexx-green-primary)"
                          : "1.5px solid var(--border-color)",
                        boxShadow: "var(--shadow-xs)",
                        transition: "border 0.2s, box-shadow 0.2s",
                        height: "100%"
                      }}
                    >
                      <CardContent sx={{ pb: "16px !important" }}>
                        {/* Card header row */}
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
                          <Box
                            sx={{
                              width: 28, height: 28, borderRadius: "6px",
                              bgcolor: "var(--mexx-green-primary)", color: "var(--text-on-primary)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 700, flexShrink: 0
                            }}
                          >
                            {item.id}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, color: "var(--text-primary)", lineHeight: 1.3 }}>
                            {item.label}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            <Chip
                              label={catLabel}
                              size="small"
                              sx={{ bgcolor: "var(--mexx-bg-surface)", color: "var(--text-primary)", fontWeight: 600, fontSize: 10, height: 20 }}
                            />
                            {isChanged && (
                              <Chip
                                label="Editado"
                                size="small"
                                sx={{
                                  bgcolor: "var(--mexx-green-subtle)",
                                  color: "var(--mexx-green-primary)",
                                  border: "1px solid var(--mexx-green-primary)",
                                  fontWeight: 600,
                                  fontSize: 10,
                                  height: 20
                                }}
                              />
                            )}
                            {error ? (
                              <ErrorOutlineIcon sx={{ color: "var(--status-error)", fontSize: 18 }} />
                            ) : item.message ? (
                              <CheckCircleOutlineIcon sx={{ color: "var(--mexx-green-primary)", fontSize: 18 }} />
                            ) : null}
                          </Box>
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Placeholder badges */}
                        {item.requiredPlaceholders.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                            {item.requiredPlaceholders.map((ph) => {
                              return (
                                <Chip
                                  key={ph}
                                  label={ph}
                                  size="small"
                                  sx={{
                                    bgcolor: "var(--mexx-green-primary)",
                                    color: "var(--text-on-primary)",
                                    border: "1px solid",
                                    borderColor: "var(--mexx-green-primary)",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    height: 22
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}

                        {/* Editor */}
                        <PlaceholderProtectedTextarea
                          value={item.message}
                          requiredPlaceholders={item.requiredPlaceholders}
                          error={error}
                          disabled={loading || saving}
                          onChange={(text) => this.handleMessageChange(item.id, text)}
                          placeholder={`Digite a mensagem para "${item.label}"...`}
                        />

                        {!error && item.requiredPlaceholders.length === 0 && (
                          <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 0.5, display: "block" }}>
                            Sem placeholders obrigatórios.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* ── Bottom action bar (mirror) ───────────────────── */}
            <Box
              sx={{
                borderRadius: "12px",
                p: 2,
                mt: 3,
                mb: 1,
                color: "var(--text-primary)",
                backgroundColor: "var(--mexx-bg-elevated)",
                border: "var(--border-default)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                boxShadow: "var(--shadow-sm)"
              }}
            >
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Mensagens de Modelo do Provedor
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.8, marginTop: 2, display: "block" }}>
                  Revise os campos e salve as alterações.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Tooltip title="Recarregar configurações">
                  <span>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<RefreshIcon />}
                      disabled={loading || saving}
                      onClick={this.loadConfiguration}
                      sx={{
                        color: "var(--mexx-green-primary)",
                        borderColor: "var(--mexx-green-primary)",
                        "&:hover": {
                          borderColor: "var(--mexx-green-primary)",
                          backgroundColor: "var(--mexx-green-subtle)"
                        }
                      }}
                    >
                      Recarregar
                    </Button>
                  </span>
                </Tooltip>

                <Badge badgeContent={changedCount || null} color="warning">
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!this.canSave()}
                    onClick={this.handleSubmit}
                    sx={{
                      bgcolor: "var(--mexx-green-primary)",
                      color: "var(--text-on-primary)",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "var(--mexx-green-dark)" },
                      "&:disabled": { bgcolor: "var(--mexx-gray-300)", color: "var(--text-muted)" }
                    }}
                  >
                    Salvar
                  </Button>
                </Badge>
              </Box>
            </Box>

            {(loading || saving) && (
              <Overlay>
                <CircularProgress sx={{ color: "var(--mexx-green-primary)" }} />
              </Overlay>
            )}

            <CustomizedSnackbars
              variant={this.state.notificationVariant}
              message={this.state.notificationMessage}
              isOpen={this.state.openNotification}
              toClose={this.closeNotification}
            />
          </Container>
        </main>

        <Footer />
      </div>
    );
  }
}

ProviderPredefinedMessageTemplates.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession
});

export default connect(mapStateToProps)(
  withStyles(Styles)(ProviderPredefinedMessageTemplates)
);
